import Order from "../../interfaces/forms/Order";
import Filter from "../../interfaces/forms/Filter";
import {FormGeneralHelper} from "../../helpers/FormGeneral";
import {ItemLoaders} from "../../core/loaders/ItemLoaders";
import moment from "moment";
import i18next from "i18next";
import {ExportToCsv} from "export-to-csv";
import {CdrList} from "../../interfaces/platform/CdrList";
import {DateHelperTs} from "../../helpers/DateHelperTs";
import i18n from "../../core/i18n";
import {checkEventForCount} from "../../helpers/BillingsHelper";
import {CurrencyHelper} from "../../helpers/CurrencyHelper";
import {Auth} from "../auth/Auth";

const _ = require('underscore');

export interface BillingResultToCsvInterface {
    realDst: string;
    realSrc: string;
    dst: string;
    src: string;
    roaming: string;
    calldate: string;
    cost: string;
    cost2: string;
    incoming: string;
    roamingCountry: string;
    fractionname: string | null;
    fractionname2: string | null;
    linearBillingName: string | null;
    linearBillingName2: string | null;
    priceNoDiscount: string;
    pricePrepaid: string;
    usedDiscountsInfo: string | null;
    usedDiscountsInfo2: string | null;
    amountDuration: string;
    amountDurationNoDiscount: string;
}

export class BillingResultToCsv {
    private readonly itemLoader: ItemLoaders;

    constructor(
        itemLoader: ItemLoaders
    ) {
        this.itemLoader = itemLoader;
    }

    exportToCsv = (orders: Array<Order>, filters: Array<Filter>, showMainLoader: (show: boolean) => void): void => {
        const extendFilter = _.clone(filters);
        extendFilter.push({name: 'pagination', value: 'false'});
        showMainLoader(true);
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/cdr_lists')),
            'cdr_lists_list_csv',
            extendFilter,
            orders,
            (items: BillingResultToCsvInterface): void => {
                this.prepareCsv(items)
                showMainLoader(false);
            },
            {propagateDataToCallback: true, dispatchAfterCallback: false}
        );
    };

    getAmountDuration = (item: CdrList): string => {
        const eventCount = checkEventForCount(item.type, item.billsec);
        const volDown = item.volDown ? ` ( ${item.volDown}  KB )` : null;

        return volDown ? `${eventCount} ${volDown}` : eventCount;
    }

    getCostValue = (cost: number | string | null): string => {
        if (!cost) {
            return '';
        }

        return CurrencyHelper.numericToStandardPln(cost);
    }

    prepareCsv = (items: any): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'csv_list' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'Billing report',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18n.t('tariffing:h.date'),
                i18n.t('tariffing:h.real_source_number'),
                i18n.t('tariffing:h.real_target_number'),
                i18n.t('tariffing:h.source_number'),
                i18n.t('tariffing:h.target_number'),
                i18n.t('tariffing:h.roaming?'),
                i18n.t('tariffing:h.roaming_country'),
                i18n.t('tariffing:h.billing_type'),
                i18n.t('tariffing:h.billing_type2'),
                i18n.t('tariffing:h.pricelist_mtr_ftr'),
                i18n.t('tariffing:h.pricelist_host'),
                i18n.t('tariffing:h.discount'),
                i18n.t('tariffing:h.discount2'),
                i18n.t('tariffing:h.direction'),
                i18n.t('tariffing:h.amount_duration'),
                i18n.t('tariffing:h.amount_duration_no_discount'),
                i18n.t('tariffing:h.cost_host', {replace: {x: Auth.getCurrencySign()}}),
                i18n.t('tariffing:h.cost_mtr_ftr', {replace: {x: Auth.getCurrencySign()}}),
                i18n.t('tariffing:h.retail_cost', {replace: {x: Auth.getCurrencySign()}}),
                i18n.t('tariffing:h.cost_no_discount', {replace: {x: Auth.getCurrencySign()}}),
            ],
        };
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvList);
    }

    prepareCsvData = (items: any): Array<BillingResultToCsvInterface> => {
        const csvList: Array<BillingResultToCsvInterface> = [];
        if (items['hydra:member'] && items['hydra:totalItems'] && items['hydra:totalItems'] > 0) {
            items['hydra:member'].map((item: CdrList) => {
                csvList.push({
                    calldate: DateHelperTs.format(item.calldate),
                    src: item.src,
                    dst: item.dst,
                    realSrc: item.realSrc,
                    realDst: item.realDst,
                    roaming: (item.cn === null || item.cn === 'PL') && item.type !== 7 ? i18n.t('general:common.No') : i18n.t('general:common.Yes'),
                    roamingCountry: item.cn === null || item.cn === 'PL' ? i18n.t('general:common.none') : item.cn,
                    fractionname: item.fractionname ?? '',
                    fractionname2: item.fractionname2 ?? '',
                    linearBillingName: item.linearBillingName ?? '',
                    linearBillingName2: item.linearBillingName2 ?? '',
                    usedDiscountsInfo: item.usedDiscountsInfo ?? '',
                    usedDiscountsInfo2: item.usedDiscountsInfo2 ?? '',
                    incoming: item.incoming ? i18n.t('tariffing:h.incoming') : i18n.t('tariffing:h.outgoing'),
                    amountDuration: this.getAmountDuration(item),
                    amountDurationNoDiscount: checkEventForCount(item.type, item.billsecNoDiscount),
                    cost: this.getCostValue(item.cost),
                    cost2: this.getCostValue(item.cost2),
                    pricePrepaid: this.getCostValue(item.pricePrepaid),
                    priceNoDiscount: this.getCostValue(item.priceNoDiscount),
                });
            });
        }
        return csvList;
    };
}